import { useCallback, useEffect, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useFlagsService } from '@atlassian/jira-flags';
import type { flag_issueViewRedaction_RedactionFlag$key } from '@atlassian/jira-relay/src/__generated__/flag_issueViewRedaction_RedactionFlag.graphql';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import messages from './messages.tsx';
import { getLastViewedTimestamp, setLastViewedTimestamp } from './utils.tsx';

type Props = {
	issue: flag_issueViewRedaction_RedactionFlag$key;
};

export const RedactionFlag = ({ issue }: Props) => {
	const { showFlag } = useFlagsService();
	const issueKey = useIssueKey();

	const { lastRedactionTime } = useFragment<flag_issueViewRedaction_RedactionFlag$key>(
		graphql`
			fragment flag_issueViewRedaction_RedactionFlag on JiraIssue {
				lastRedactionTime
			}
		`,
		issue,
	);

	const shouldShowFlag = useMemo(() => {
		if (lastRedactionTime) {
			const lastViewedTimestamp = getLastViewedTimestamp(issueKey);
			const lastRedactedTimeStamp = lastRedactionTime ? new Date(lastRedactionTime).getTime() : 0;
			return lastViewedTimestamp < lastRedactedTimeStamp;
		}
		return false;
	}, [issueKey, lastRedactionTime]);

	const handleShowFlag = useCallback(() => {
		showFlag({
			id: 'redaction-flag',
			isAutoDismiss: false,
			type: 'info',
			title: messages.title,
			messageId: 'issue-view-redaction.ui.flag.show-flag.info',
			messageType: 'transactional',
			actions: [
				{
					content: messages.link,
					href: 'https://support.atlassian.com/jira-software-cloud/docs/how-redaction-works/',
					target: '_blank',
				},
			],
		});
	}, [showFlag]);

	useEffect(() => {
		if (shouldShowFlag) {
			handleShowFlag();
			setLastViewedTimestamp(issueKey);
		}
	}, [handleShowFlag, issueKey, shouldShowFlag]);

	return null;
};
